/* report.js — Génère les sorties d'une analyse : rapport HTML autonome (styles
 * inline, aucune ressource réseau) pour un fichier ou un lot, résumé texte et
 * CSV du lot. Tout contenu issu des fichiers est échappé. Expose self.MediaReport. */
'use strict';

import F from './format.js';
import K from './findings.js';


function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
    return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
  });
}

var STYLE =
  ':root{--ink:#16191D;--muted:#5C6670;--line:#E2E6E9;--bg:#F4F6F7;--surface:#fff;--accent:#0F7B8A}' +
  '*{box-sizing:border-box;margin:0;padding:0}' +
  'body{font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:var(--ink);background:var(--bg);line-height:1.5;padding:32px 16px}' +
  '.doc{max-width:820px;margin:0 auto}' +
  '.mono{font-family:ui-monospace,Menlo,Consolas,monospace}' +
  'h1{font-size:1.6rem;letter-spacing:-.02em}h2{font-size:1rem;margin:0 0 8px}' +
  '.smpte{display:flex;height:8px;border-radius:3px;overflow:hidden;margin:14px 0 20px}' +
  '.smpte span{flex:1}.smpte i:nth-child(1){}' +
  '.eyebrow{font-family:ui-monospace,monospace;font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;color:var(--muted)}' +
  '.card{background:var(--surface);border:1px solid var(--line);border-radius:10px;padding:16px 18px;margin-top:14px}' +
  '.synthese{background:#E3F0F2;border-color:#CFE6E9;font-size:1.05rem}' +
  '.meta{font-family:ui-monospace,monospace;font-size:.75rem;color:var(--muted);margin-top:4px}' +
  '.row{display:grid;grid-template-columns:230px 1fr;gap:12px;padding:6px 0;border-bottom:1px solid var(--bg);font-size:.9rem}' +
  '.row:last-child{border-bottom:none}.row .k{color:var(--muted)}.row .v{font-family:ui-monospace,monospace;font-size:.82rem;word-break:break-word}' +
  '.verdict{border-radius:8px;padding:12px 14px;border:1px solid;font-weight:700}' +
  '.verdict.conforme{background:#E7F6EC;border-color:#B7E0C4}.verdict.conforme-avert{background:#FBF2DE;border-color:#EAD6A6}' +
  '.verdict.non-conforme{background:#FBE7E7;border-color:#EBB9B9}.verdict.indeterminate{background:#EEF1F3;border-color:#D5DCE1}' +
  'ul{list-style:none}li{padding:6px 0;border-top:1px solid var(--bg);font-size:.88rem;display:flex;gap:8px}li:first-child{border-top:none}' +
  '.mk{width:16px;flex:none;font-family:ui-monospace,monospace}.pass{color:#2E7D46}.warning{color:#9A6A10}.error{color:#C0392B}.unevaluable,.info{color:#5C6670}' +
  'table{width:100%;border-collapse:collapse;font-size:.84rem}th,td{text-align:left;padding:8px 10px;border-bottom:1px solid var(--bg);vertical-align:top}' +
  'th{font-family:ui-monospace,monospace;font-size:.68rem;text-transform:uppercase;color:var(--muted)}' +
  'footer{margin-top:22px;color:var(--muted);font-size:.78rem;border-top:1px solid var(--line);padding-top:12px}' +
  'details{margin-top:10px;font-size:.88rem}summary{cursor:pointer;color:var(--muted)}' +
  'h3{font-size:.9rem;margin:14px 0 4px}.gap{display:block;padding:10px 0}.gap-title{font-weight:700}' +
  '.gap dl{display:grid;grid-template-columns:110px 1fr;gap:2px 12px;margin-top:6px}.gap dt{color:var(--muted)}' +
  '.sev{font-family:ui-monospace,monospace;font-size:.7rem;text-transform:uppercase;margin-right:6px}' +
  '.note{margin:0 0 6px}.origin{font-size:.65rem;text-transform:uppercase;letter-spacing:.06em;padding:1px 6px;border-radius:999px;background:#E3F0F2;color:var(--accent);margin-left:6px}';

var SMPTE = '<div class="smpte">' +
  '<span style="background:#C0C0C0"></span><span style="background:#C0C000"></span><span style="background:#00C0C0"></span>' +
  '<span style="background:#00C000"></span><span style="background:#C000C0"></span><span style="background:#C00000"></span>' +
  '<span style="background:#0000C0"></span></div>';

function verdictBlock(rep) {
  if (!rep) return '';
  var v = rep.verdict;
  var meta = 'profil ' + esc(rep.profile.id) + ' v' + esc(rep.profile.version || '?') +
    (rep.profile.date ? ' (' + esc(rep.profile.date) + ')' : '') + ' · moteur v' + esc(rep.engine) +
    (rep.profile.statut ? ' · ' + esc(rep.profile.statut) : '');
  var rules = '';
  if (!rep.applicable.ok) rules = '<p class="meta">' + esc(rep.applicable.reason) + '</p>';
  else {
    var q = F.quietRules(rep);
    rules = quietList(q.unevaluable, 'unevaluable', '?', K.LABELS.rulesUnevaluable, v.code === 'indeterminate') +
      quietList(q.passed, 'pass', '✓', K.LABELS.rulesPassed, false) +
      quietList(q.notApplicable, 'unevaluable', '–', K.LABELS.rulesNotApplicable, false);
  }
  return '<div class="card"><h2>Contrôle de conformité</h2>' +
    '<div class="verdict ' + v.code + '">' + esc(v.label) + '</div><p class="meta">' + meta + '</p>' +
    (rep.profile.description ? '<p class="meta">' + esc(rep.profile.description) + '</p>' : '') +
    (rep.profile.notChecked && rep.profile.notChecked.length
      ? '<p class="meta">' + esc(K.LABELS.notChecked) + ' :</p><ul>' +
        rep.profile.notChecked.map(function (n) { return '<li>' + esc(n) + '</li>'; }).join('') + '</ul>' : '') +
    sourcesHtml(rep.profile.sources) + rules + '</div>';
}

function sourcesHtml(sources) {
  if (!sources || !sources.length) return '';
  return '<p class="meta">' + esc(K.LABELS.sources) + ' :</p><ul>' + sources.map(function (s) {
    return '<li class="meta">' + esc(s.title) + ' — ' + esc(s.url) + (s.consulted ? ' (' + esc(K.fill(K.LABELS.consulted, { date: s.consulted })) + ')' : '') + '</li>';
  }).join('') + '</ul>';
}

function quietList(lines, status, mark, label, open) {
  if (!lines.length) return '';
  return '<details' + (open ? ' open' : '') + '><summary>' + esc(label) + ' · ' + lines.length + '</summary><ul>' +
    lines.map(function (r) {
      return '<li><span class="mk ' + status + '">' + mark + '</span><span>' + esc(r.message) +
        (r.observed ? ' <span class="meta">(' + esc(r.observed) + ')</span>' : '') + '</span></li>';
    }).join('') + '</ul></details>';
}

function gapsBlock(view) {
  if (!view.total) return '<div class="card gaps"><h2>' + esc(view.title) + '</h2><p class="meta">' + esc(view.empty) + '</p></div>';
  var L = view.labels;
  function row(k, v) { return v ? '<dt>' + esc(k) + '</dt><dd>' + esc(v) + '</dd>' : ''; }
  return '<div class="card gaps"><h2>' + esc(view.title) + ' · ' + view.total + '</h2>' +
    view.groups.map(function (g) {
      return '<h3>' + esc(g.label) + ' · ' + g.items.length + '</h3><ul>' + g.items.map(function (it) {
        return '<li class="gap"><span class="sev ' + it.severity + '">' + esc(it.severityLabel) + '</span>' +
          '<span class="gap-title">' + esc(it.title) + '</span>' +
          '<span class="meta"> · ' + esc(it.subject) + (it.domain ? ' · ' + esc(it.domain) : '') + '</span>' +
          '<dl>' + row(L.expected, it.expected) + row(L.observed, it.observed.join(' ; ')) +
          row(L.why, it.explanation) + row(L.correction, it.correction) + row(L.confidence, it.confidence) +
          row(L.proof, it.proof && it.proof.text) + '</dl></li>';
      }).join('') + '</ul>';
    }).join('') + '</div>';
}

// Écarts d'un fichier, depuis ses constats et son rapport de profil.
function gapsOf(data) {
  return data.gaps || K.collect({ diagnostics: data.diags || [], report: data.report || null, model: data.model || null });
}

function privacyBlock(v) {
  return '<div class="card"><h2>' + esc(v.title) + '</h2>' + (v.empty ? '<p class="meta">' + esc(v.empty) + '</p>'
    : '<p class="meta note">' + esc(v.note) + '</p>' + v.items.map(function (it) {
      return '<div class="row"><span class="k">' + esc(it.label) + '</span><span class="v">' + esc(it.value) + '</span></div>';
    }).join('')) + '</div>';
}

function tracksBlocks(model) {
  if (!F) return '';
  return F.view(model).map(function (b) {
    var rows = b.rows.map(function (r) {
      return '<div class="row"><span class="k">' + esc(r.k) + '</span><span class="v">' + esc(r.v) +
        (r.origin ? ' <span class="origin">' + esc(K.ORIGINS[r.origin]) + '</span>' : '') + '</span></div>';
    }).join('');
    return '<div class="card"><h2>' + esc(b.title) + (b.sub ? ' <span class="meta">' + esc(b.sub) + '</span>' : '') + '</h2>' +
      '<p class="meta note">' + esc(b.note) + '</p>' + rows + '</div>';
  }).join('');
}

function integrityBlock(digest) {
  if (!digest) return '';
  return '<div class="card"><h2>Intégrité</h2>' +
    '<div class="row"><span class="k">Empreinte SHA-256 calculée</span><span class="v">' + esc(digest) + '</span></div>' +
    '<p class="meta">Empreinte calculée localement. La mention « intégrité vérifiée » ne vaut que par rapport à une empreinte de référence fournie.</p></div>';
}

function shell(title, bodyInner) {
  return '<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8">' +
    '<meta name="viewport" content="width=device-width, initial-scale=1"><title>' + esc(title) + '</title>' +
    '<style>' + STYLE + '</style></head><body><div class="doc">' + bodyInner + '</div></body></html>';
}

function buildFileReport(data) {
  var m = data.model;
  var head = '<p class="eyebrow">Rapport d’analyse média · local</p>' +
    '<h1>' + esc(data.fileName) + '</h1>' +
    '<p class="meta">' + esc(F ? (F.fmt.bytes(data.fileSize) || '') : data.fileSize) +
    ' · ' + esc(m.tracks.length) + ' piste(s) média · généré le ' + esc(data.generatedAt || '') + '</p>' + SMPTE;
  var cover = (F && F.coverImg) ? F.coverImg(m) : '';
  var chapters = (F && F.chaptersHtml) ? F.chaptersHtml(m) : '';
  var body = head +
    (cover ? '<div class="card">' + cover + '</div>' : '') +
    '<div class="card synthese">' + esc(data.sentence) + '</div>' +
    verdictBlock(data.report) +
    gapsBlock(F.gapsView(gapsOf(data), m, !!data.report)) +
    privacyBlock(F.privacyView(m)) +
    (chapters ? '<div class="card">' + chapters + '</div>' : '') +
    integrityBlock(data.digest) +
    tracksBlocks(m) +
    '<footer>Rapport généré localement dans le navigateur. Aucune donnée n’a été transmise. Tout contenu issu du fichier est échappé.</footer>';
  return shell('Rapport — ' + data.fileName, body);
}

function buildBatchReport(items, info) {
  var head = '<p class="eyebrow">Rapport d’analyse média · lot · local</p>' +
    '<h1>Lot de ' + esc(items.length) + ' fichier(s)</h1>' +
    '<p class="meta">' + (info && info.profileName ? 'Profil : ' + esc(info.profileName) + ' · ' : '') +
    'généré le ' + esc(info && info.generatedAt || '') + '</p>' + SMPTE;
  var rows = items.map(function (it) {
    var verdict = it.report ? '<span class="' + it.report.verdict.code + '">' + esc(it.report.verdict.label) + '</span>' : '—';
    var n = F.gapCounts(gapsOf(it));
    return '<tr><td>' + esc(it.fileName) + '</td><td>' + esc(it.container || '—') + '</td>' +
      '<td>' + esc(it.sentence || '') + '</td><td>' + verdict + '</td>' +
      '<td>' + n.fix + '</td><td>' + n.check + '</td><td>' + n.optional + '</td></tr>';
  }).join('');
  var P = K.PRIORITIES;
  var body = head + '<div class="card"><table><thead><tr><th>Fichier</th><th>Conteneur</th><th>Synthèse</th><th>Verdict</th>' +
    '<th>' + esc(P.fix) + '</th><th>' + esc(P.check) + '</th><th>' + esc(P.optional) + '</th></tr></thead><tbody>' +
    rows + '</tbody></table></div>' +
    '<footer>Rapport de lot généré localement. Aucune donnée transmise. Contenu des fichiers échappé.</footer>';
  return shell('Rapport de lot (' + items.length + ')', body);
}

/* ---------- Résumé texte (bouton « Copier le résumé ») ---------- */
function buildSummaryText(data) {
  var m = data.model, rep = data.report;
  var lines = [data.sentence, ''];
  if (rep) {
    var p = rep.profile;
    lines.push('— Contrôle de conformité —');
    lines.push('Profil : ' + p.name + ' (' + p.id + ' v' + (p.version || '?') + ', ' + (p.statut || '') + ') · moteur v' + rep.engine);
    lines.push('Verdict : ' + rep.verdict.label);
    (p.sources || []).forEach(function (src) { lines.push(K.LABELS.sources + ' : ' + src.title + ' — ' + src.url + (src.consulted ? ' (' + K.fill(K.LABELS.consulted, { date: src.consulted }) + ')' : '')); });
    if (!rep.applicable.ok) lines.push(rep.applicable.reason);
    else {
      var q = F.quietRules(rep);
      q.unevaluable.forEach(function (r) { lines.push('[?] ' + r.message + (r.observed ? ' (' + r.observed + ')' : '')); });
      q.passed.forEach(function (r) { lines.push('[✓] ' + r.message + (r.observed ? ' (' + r.observed + ')' : '')); });
      q.notApplicable.forEach(function (r) { lines.push('[–] ' + r.message); });
    }
    lines.push('');
  }
  var view = F.gapsView(gapsOf(data), m, !!rep);
  var L = view.labels;
  lines.push('— ' + view.title + ' —');
  if (!view.total) lines.push(view.empty);
  view.groups.forEach(function (g) {
    lines.push(g.label + ' (' + g.items.length + ')');
    g.items.forEach(function (it) {
      lines.push('- [' + it.severityLabel + '] ' + it.title + ' · ' + it.subject + (it.domain ? ' · ' + it.domain : ''));
      if (it.expected) lines.push('  ' + L.expected + ' : ' + it.expected);
      if (it.observed.length) lines.push('  ' + L.observed + ' : ' + it.observed.join(' ; '));
      if (it.explanation) lines.push('  ' + L.why + ' : ' + it.explanation);
      if (it.correction) lines.push('  ' + L.correction + ' : ' + it.correction);
      if (it.confidence) lines.push('  ' + L.confidence + ' : ' + it.confidence);
      if (it.proof) lines.push('  ' + L.proof + ' : ' + it.proof.text);
    });
  });
  lines.push('');
  lines.push(F.summaryText(m, data.fileName));
  return lines.join('\n');
}

/* ---------- Export CSV du lot (cellules =+-@ neutralisées) ---------- */
function csvCell(v) {
  if (v == null) v = '';
  v = String(v);
  if (/^[=+\-@\t\r]/.test(v)) v = "'" + v;      // neutralisation formule
  if (/[",\n;]/.test(v)) v = '"' + v.replace(/"/g, '""') + '"';
  return v;
}

// items : { fileName, fileSize, model, sentence, diags, report }
function buildBatchCsv(items) {
  var P = K.PRIORITIES;
  var header = ['Fichier', 'Taille (o)', 'Conteneur', 'Durée (s)', 'Pistes', 'Verdict', 'Diagnostics', 'Avertissements', 'Synthèse',
    P.fix, P.check, P.optional];
  var lines = [header.map(csvCell).join(',')];
  items.forEach(function (it) {
    var c = it.model.container;
    var warn = it.diags.filter(function (d) { return d.level === 'warning'; }).length;
    var n = F.gapCounts(gapsOf(it));
    lines.push([
      it.fileName, it.fileSize, c.format || '', c.duration != null ? c.duration : '',
      it.model.tracks.length, it.report ? it.report.verdict.label : '', it.diags.length, warn, it.sentence,
      n.fix, n.check, n.optional
    ].map(csvCell).join(','));
  });
  return '\ufeff' + lines.join('\r\n');
}

var api = {
  buildFileReport: buildFileReport, buildBatchReport: buildBatchReport,
  buildSummaryText: buildSummaryText, buildBatchCsv: buildBatchCsv,
  esc: esc, _gapsBlock: gapsBlock
};
globalThis.MediaReport = api;
export default api;
